/**
 * Supabase Client for Registry-Demo
 * 
 * Provides client-side Supabase client for browser use.
 * Uses anon key for client-side operations with RLS.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { withWorkingCycle } from './itemCycleContext';
import {
  crossTabAuthLock,
  extractSentRefreshToken,
  isRefreshGrantRequest,
  readStoredRefreshTokens,
  readStoredSessionKeys,
  shouldClearStoredSession,
} from './authSessionGuard';

// Module-level ref — may be reset by Vite HMR. See window key below.
let supabaseClient: SupabaseClient | null = null;
// Window key for the singleton — survives Vite HMR module resets so the same
// client instance is always used. Without this, HMR resets the module variable
// above to null, a second createClient() fires, and Supabase warns
// "Multiple GoTrueClient instances detected" which reintroduces lock contention.
const WINDOW_KEY = '__ppSupabaseClient';

/**
 * Gets environment variable from Vite context
 */
function getEnvVar(name: string): string | undefined {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // import.meta.env is available in Vite/browser contexts
      return import.meta.env[name];
    }
  } catch {
    // import.meta not available, skip
  }
  return undefined;
}

/**
 * Creates or returns a singleton Supabase client for client-side use.
 * 
 * Uses VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from environment.
 * 
 * @returns Supabase client instance
 * @throws Error if required environment variables are missing
 */
export function getSupabaseClient(): SupabaseClient {
  // Check window-level cache first so HMR module resets don't spawn a second client
  const win = typeof window !== 'undefined' ? (window as Record<string, unknown>) : null;
  if (win?.[WINDOW_KEY]) {
    supabaseClient = win[WINDOW_KEY] as SupabaseClient;
    return supabaseClient;
  }
  if (supabaseClient) {
    return supabaseClient;
  }

  const supabaseUrl = getEnvVar('VITE_SUPABASE_URL');
  const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY');

  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL environment variable is required');
  }

  if (!supabaseAnonKey) {
    throw new Error('VITE_SUPABASE_ANON_KEY environment variable is required');
  }

  supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      // Serializes auth work across tabs. Supabase coalesces refreshes per client,
      // so without this a tab can refresh with a token other tabs already replaced;
      // the server can refuse it, and then the whole browser is signed out. See
      // authSessionGuard.ts. It queues rather than aborts, so concurrent store
      // fetches wait instead of throwing "Lock was stolen".
      lock: crossTabAuthLock,
    },
    db: {
      // Avoid AbortError from default ~8s timeout when many tables load in parallel
      timeout: 60_000,
    },
    global: {
      // Custom fetch to handle Safari ITP, network issues, and stale auth
      fetch: async (url, options = {}) => {
        const urlString = typeof url === 'string' ? url : url.toString();
        // Only a refresh grant says anything about the stored session. A password
        // grant that 400s means a mistyped password, which must not touch it.
        const isTokenRefresh = isRefreshGrantRequest(urlString);
        try {
          const response = await fetch(url, {
            ...withWorkingCycle(urlString, options),
            // Use 'omit' to avoid CORS issues with credentials in Safari
            credentials: 'omit',
          });
          // A refresh 400 must clear storage AND the in-memory session, or the client
          // keeps thinking it is logged in and sign-in stays broken. But not when this
          // request only lost a race: a different stored token means another tab
          // already refreshed, and that session is healthy.
          if (isTokenRefresh && response.status === 400) {
            setTimeout(() => {
              try {
                if (typeof localStorage === 'undefined') return;
                const sent = extractSentRefreshToken(options.body);
                if (!shouldClearStoredSession(sent, readStoredRefreshTokens(localStorage))) return;
                for (const key of readStoredSessionKeys(localStorage)) localStorage.removeItem(key);
                if (supabaseClient) {
                  void supabaseClient.auth.signOut({ scope: 'local' });
                }
              } catch (_) { /* ignore */ }
            }, 0);
          }
          return response;
        } catch (error) {
          console.error('[Supabase] Fetch error:', error);
          throw error;
        }
      },
    },
  });

  // Persist on window so HMR reloads of this module reuse the same instance
  if (win) win[WINDOW_KEY] = supabaseClient;

  return supabaseClient;
}

/**
 * Checks if Supabase is configured with the required environment variables.
 * 
 * @returns true if both VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set
 */
export function isSupabaseConfigured(): boolean {
  const supabaseUrl = getEnvVar('VITE_SUPABASE_URL');
  const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY');
  return !!(supabaseUrl && supabaseAnonKey);
}
